"use client"

import type React from "react"

import {
  Button,
  Input,
  HStack,
  VStack,
  Box,
  Text,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  PopoverHeader,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { BoxTitle } from "@/components/BoxTitle"
import { FieldTipoDeposito } from "@/components/Fields/TipoDeposito"
import { OptionGroup } from "@/components/FormFields/OptionGroup"

import { MdCheckCircle, MdCircle } from "react-icons/md" 
import { FiEdit3 } from "react-icons/fi"
import { useState } from "react"


interface FilterCargaData {
  depositoerp: number | null
  nome: string
  tipo: number
  ativo: boolean
  situacao: string[]
  tipoCarga: string[]
  prioridade: string[]
  periodoEstadiaInicial?: string | null 
  periodoEstadiaFinal?: string | null 
  dataChegadaInicial?: string | null
  dataChegadaFinal?: string | null
  dataEntradaInicial?: string | null
  dataEntradaFinal?: string | null
  dataSaidaInicial?: string | null
  dataSaidaFinal?: string | null
}

interface FilterCargaProps {
  children: React.ReactNode;
  onFilter: (data: FilterCargaData) => void;
}

const FilterCarga: React.FC<FilterCargaProps> = ({ children, onFilter }) => {
  const [isPeriodoEstadiaSelected, setIsPeriodoEstadiaSelected] = useState(true)
  const [isDataChegadaSelected, setIsDataChegadaSelected] = useState(false)
  const [isDataEntradaSelected, setIsDataEntradaSelected] = useState(false)
  const [isDataSaidaSelected, setIsDataSaidaSelected] = useState(true)

  const { handleSubmit, setValue, watch, reset, control } = useForm<FilterCargaData>({
    defaultValues: {
      depositoerp: null,
      nome: "",
      tipo: 0,
      ativo: true,
      situacao: [],
      tipoCarga: [],
      prioridade: [],
      periodoEstadiaInicial: null,
      periodoEstadiaFinal: null,
      dataChegadaInicial: null,
      dataChegadaFinal: null,
      dataEntradaInicial: null,
      dataEntradaFinal: null,
      dataSaidaInicial: null,
      dataSaidaFinal: null,
    },
  })

  const safeNumberConversion = (value: any): number | null => {
    if (value === undefined || value === null || value === "") {
      return null
    }

    const numValue = Number(value)
    if (isNaN(numValue)) {
      return null
    }

    return numValue
  }

  const onSubmit = (formData: FilterCargaData) => {
    const parsedData = {
      ...formData,
      periodoEstadiaInicial: isPeriodoEstadiaSelected ? formData.periodoEstadiaInicial : null,
      periodoEstadiaFinal: isPeriodoEstadiaSelected ? formData.periodoEstadiaFinal : null,
      dataChegadaInicial: isDataChegadaSelected ? formData.dataChegadaInicial : null,
      dataChegadaFinal: isDataChegadaSelected ? formData.dataChegadaFinal : null,
      dataEntradaInicial: isDataEntradaSelected ? formData.dataEntradaInicial : null,
      dataEntradaFinal: isDataEntradaSelected ? formData.dataEntradaFinal : null,
      dataSaidaInicial: isDataSaidaSelected ? formData.dataSaidaInicial : null,
      dataSaidaFinal: isDataSaidaSelected ? formData.dataSaidaFinal : null,
    }
    onFilter(parsedData);
  }

  const getPopoverTitle = () => {
    return "ACOMPANHAMENTO DE AGENDAMENTO - CARGA"
  }

  const renderHeaderContent = () => (
    <PopoverHeader bg="#117baa" color="white" borderTopRightRadius="3xl" borderTopLeftRadius="3xl" m={0} p={1} fontSize="15px">
      <HStack>
        <Box as={FiEdit3} color="#117baa" boxSize={7} p={1} bg="#DBF2FB" rounded="full" />
        <Text fontSize="15px" color="white" fontWeight="bold">
          {getPopoverTitle()}
        </Text>
      </HStack>
    </PopoverHeader>
  )

  const situacaoOptions = ["Agendado chegada", "Liderado p/doca", "Chegada confirmada", "Entrada confirmada", "Saída confirmada", "Finalizadas"]

  const renderBodyContent = () => {
    return (
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
          <BoxTitle title="Contratação">
            <FieldTipoDeposito
              isInvalid={false}
              labelOf
              error={undefined}
              //@ts-ignore
              value={watch("tipo")}
              onChange={(val: string) => {
                const numValue = safeNumberConversion(val)
                setValue("tipo", numValue || 0)
              }}
              required
            />
          </BoxTitle>
        

          <HStack spacing={6}  justifyContent="center" width="100%">
            <VStack spacing={4} align="center">
              <HStack spacing={1} align="center" cursor="pointer" onClick={() => setIsPeriodoEstadiaSelected(!isPeriodoEstadiaSelected)}>
                {isPeriodoEstadiaSelected ? <MdCheckCircle size="24px" color="#117BAA" /> : <MdCircle size="24px" color="#ccc" />}
                <Text fontWeight="bold" color={isPeriodoEstadiaSelected ? "#117BAA" : "gray.600"}>Período Estadia</Text>
              </HStack>
              <BoxTitle title="Data">
                <VStack py={4} px={1} border="1px solid #ccc" rounded="md"  opacity={isPeriodoEstadiaSelected ? 1 : 0.5}>
                  <BoxTitle title="Inicial">
                    <Input
                      placeholder='Inicial'
                      size='md'
                      type='date'
                      value={watch("periodoEstadiaInicial") || ''}
                      onChange={(e) => setValue("periodoEstadiaInicial", e.target.value)}
                      isDisabled={!isPeriodoEstadiaSelected}
                    />
                  </BoxTitle>
                  <BoxTitle title="Final">
                    <Input
                      placeholder='Final'
                      size='md'
                      type='date'
                      value={watch("periodoEstadiaFinal") || ''}
                      onChange={(e) => setValue("periodoEstadiaFinal", e.target.value)}
                      isDisabled={!isPeriodoEstadiaSelected}
                    />
                  </BoxTitle>
                </VStack>
              </BoxTitle>
            </VStack>

            <VStack spacing={4} align="center">
              <HStack spacing={1} align="center" cursor="pointer" onClick={() => setIsDataChegadaSelected(!isDataChegadaSelected)}>
                {isDataChegadaSelected ? <MdCheckCircle size="24px" color="#117BAA" /> : <MdCircle size="24px" color="#ccc" />}
                <Text fontWeight="bold" color={isDataChegadaSelected ? "#117BAA" : "gray.600"}>Data Chegada</Text>
              </HStack>
              <BoxTitle title="Data">
                <VStack py={4} px={1} border="1px solid #ccc" rounded="md"  opacity={isDataChegadaSelected ? 1 : 0.5}>
                  <BoxTitle title="Inicial">
                    <Input
                      placeholder='Inicial'
                      size='md'
                      type='date'
                      value={watch("dataChegadaInicial") || ''}
                      onChange={(e) => setValue("dataChegadaInicial", e.target.value)}
                      isDisabled={!isDataChegadaSelected}
                    />
                  </BoxTitle>
                  <BoxTitle title="Final">
                    <Input
                      placeholder='Final'
                      size='md'
                      type='date'
                      value={watch("dataChegadaFinal") || ''}
                      onChange={(e) => setValue("dataChegadaFinal", e.target.value)}
                      isDisabled={!isDataChegadaSelected}
                    />
                  </BoxTitle>
                </VStack>
              </BoxTitle>
            </VStack>

            <VStack spacing={4} align="center">
              <HStack spacing={1} align="center" cursor="pointer" onClick={() => setIsDataEntradaSelected(!isDataEntradaSelected)}>
                {isDataEntradaSelected ? <MdCheckCircle size="24px" color="#117BAA" /> : <MdCircle size="24px" color="#ccc" />}
                <Text fontWeight="bold" color={isDataEntradaSelected ? "#117BAA" : "gray.600"}>Data Entrada</Text>
              </HStack>
              <BoxTitle title="Data">
                <VStack py={4} px={1} border="1px solid #ccc" rounded="md"  opacity={isDataEntradaSelected ? 1 : 0.5}>
                  <BoxTitle title="Inicial">
                    <Input
                      placeholder='Inicial'
                      size='md'
                      type='date'
                      value={watch("dataEntradaInicial") || ''}
                      onChange={(e) => setValue("dataEntradaInicial", e.target.value)}
                      isDisabled={!isDataEntradaSelected}
                    />
                  </BoxTitle>
                  <BoxTitle title="Final">
                    <Input
                      placeholder='Final'
                      size='md'
                      type='date'
                      value={watch("dataEntradaFinal") || ''}
                      onChange={(e) => setValue("dataEntradaFinal", e.target.value)}
                      isDisabled={!isDataEntradaSelected}
                    />
                  </BoxTitle>
                </VStack>
              </BoxTitle>
            </VStack>

            <VStack spacing={4} align="center">
              <HStack spacing={1} align="center" cursor="pointer" onClick={() => setIsDataSaidaSelected(!isDataSaidaSelected)}>
                {isDataSaidaSelected ? <MdCheckCircle size="24px" color="#117BAA" /> : <MdCircle size="24px" color="#ccc" />}
                <Text fontWeight="bold" color={isDataSaidaSelected ? "#117BAA" : "gray.600"}>Data Saída</Text>
              </HStack>
              <BoxTitle title="Data">
                <VStack py={4} px={1} border="1px solid #ccc" rounded="md"  opacity={isDataSaidaSelected ? 1 : 0.5}>
                  <BoxTitle title="Inicial">
                    <Input
                      placeholder='Inicial'
                      size='md'
                      type='date'
                      value={watch("dataSaidaInicial") || ''}
                      onChange={(e) => setValue("dataSaidaInicial", e.target.value)}
                      isDisabled={!isDataSaidaSelected}
                    />
                  </BoxTitle>
                  <BoxTitle title="Final">
                    <Input
                      placeholder='Final'
                      size='md'
                      type='date'
                      value={watch("dataSaidaFinal") || ''}
                      onChange={(e) => setValue("dataSaidaFinal", e.target.value)}
                      isDisabled={!isDataSaidaSelected}
                    />
                  </BoxTitle>
                </VStack>
              </BoxTitle>
            </VStack>
          </HStack>

          <HStack w="100%" justify="center" align="center" spacing={4}>
            <Box>
              <OptionGroup
                name="situacao"
                label="Situação"
                options={situacaoOptions}
                control={control}
                fillColor="#4CAF50"
              />
            </Box>
          </HStack>
        </VStack>
      </Box>
    )
  }

  const renderFooterContent = () => {
    return (
      <HStack justify="flex-end" w="100%" mt={4}>
        <Button 
          backgroundColor={"#117baa"} 
          onClick={handleSubmit(onSubmit)} 
          color={"white"}
        >
          Filtrar
        </Button>
        <Button
          backgroundColor={"gray.300"}
          onClick={() => reset()}
          color={"gray.800"}
        >
          Limpar
        </Button>
      </HStack>
    )
  }

  return (
    <Popover placement="auto-start">
      <PopoverTrigger>
        <Box textAlign="center">{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="900px" borderTopRightRadius="3xl" borderTopLeftRadius="3xl">
          <PopoverCloseButton color="white" p="1" /> 
          {renderHeaderContent()}
          <PopoverBody p={4}>
            {renderBodyContent()}
            {renderFooterContent()}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export default FilterCarga